<!-- src/components/Thumbs.vue -->
<template>
  <div class="thumbs">
    <button
      class="thumb"
      :class="{ clicked: clickedThumb === 'up' }"
      @click="onThumbUp"
      :disabled="clickedThumb !== null"
    >
      <svg
        fill="none"
        height="24"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        viewBox="0 0 24 24"
        width="24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"
        />
      </svg>
    </button>
    <button
      class="thumb"
      :class="{ clicked: clickedThumb === 'down' }"
      @click="onThumbDown"
      :disabled="clickedThumb !== null"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"
        ></path>
      </svg>
    </button>
  </div>
  <div class="feedback-text" v-if="clickedThumb !== null">
    <form
      class="feedback-form"
      @submit.prevent="onSubmitFeedback"
      v-if="!feedbackSubmitted"
    >
      <textarea
        rows="2"
        cols="20"
        class="user-feedback"
        v-model="feedbackText"
        placeholder="Any additional feedback will help improve the service."
      ></textarea>
      <button
        type="submit"
        class="feedback-submit-button"
        :disabled="!feedbackText.trim()"
      >
        Submit
      </button>
    </form>
    <div v-else class="submitted-feedback">
      <strong>Feedback:</strong> {{ feedbackText }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import axios from 'axios';

export default defineComponent({
  name: 'Thumbs',
  props: {
    interactionId: {
      type: String,
      required: true
    },
    sessionId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const clickedThumb = ref<string | null>(null);
    const feedbackText = ref<string>('');
    const feedbackSubmitted = ref<boolean>(false);

    // eslint-disable-next-line space-before-function-paren
    const recordInteraction = async (interaction: string) => {
      try {
        const headers: { [key: string]: string } = {};

        // Check if running locally
        if (process.env.NODE_ENV === 'development') {
          headers['x-functions-key'] = process.env.VUE_APP_X_FUNCTIONS_KEY;
        }

        const body = {
          interaction: interaction,
          session_id: props.sessionId,
          interaction_id: props.interactionId
        };

        const response = await axios.post(
          '/api/answers/lbbd_answer_service_feedback',
          body,
          { headers }
        );

        if (response.status === 202) {
          console.log('Interaction recorded successfully');
        } else {
          console.error('Failed to record interaction');
        }
      } catch (error) {
        console.error('Failed to record interaction:', error);
      }
    };

    const onThumbUp = () => {
      clickedThumb.value = 'up';
      recordInteraction('up');
    };

    const onThumbDown = () => {
      clickedThumb.value = 'down';
      recordInteraction('down');
    };

    const onSubmitFeedback = () => {
      if (!feedbackText.value.trim()) {
        return;
      }
      recordInteraction(feedbackText.value);
      feedbackSubmitted.value = true;
    };

    return {
      clickedThumb,
      onThumbUp,
      onThumbDown,
      feedbackText,
      feedbackSubmitted,
      onSubmitFeedback
    };
  }
});
</script>

<style scoped>
.thumbs {
  margin-top: 5px;
  display: flex;
  gap: 10px;
}

.thumb {
  cursor: pointer;
  background: none;
  border: none;
  padding: 0px;
}

.thumb svg {
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
}

.thumb.clicked svg {
  fill: none;
  stroke: black;
  stroke-width: 2;
}

.thumb[disabled] {
  cursor: initial;
}

.feedback-form {
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin-top: 10px;
  max-width: 100%;
}

@media (max-width: 500px) {
  .feedback-form {
    flex-direction: column;
  }
}

.user-feedback {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 0px;
  flex-basis: 100%;
  font-size: 1.2rem;
}

.user-feedback::placeholder {
  color: #555;
  font-size: 1rem;
}

.feedback-submit-button {
  padding: 10px 20px;
  background-color: white;
  color: black;
  border: 2px solid rgb(0, 121, 52);
  border-radius: 0;
  cursor: pointer;
  max-height: 40px;
}

.feedback-submit-button:hover:not(:disabled) {
  border-color: black;
}

.feedback-submit-button:disabled {
  background-color: #f5f5f5;
  color: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
}

.submitted-feedback {
  margin-top: 10px;
  font-size: 1.2rem;
  color: #333;
}
</style>
